import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Building2,
  CreditCard,
  Banknote,
  Wallet,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { useAppContext } from "@/providers";
import { useToast } from "@/components/ui/toast";
import { t } from "@/lib/i18n/messages";
import { formatCurrency } from "@/lib/utils/format";
import { topUpWallet } from "@/lib/api/wallet";
import { cn } from "@/lib/utils";

// ── Constants ─────────────────────────────────────────────────────────────────

const FEE_RATE_PERCENT = 1.5;
const PRESET_AMOUNTS = [500, 1000, 2500, 5000];

type PaymentMethodId = "bank_transfer" | "credit_card" | "cash";

interface PaymentMethod {
  id: PaymentMethodId;
  icon: LucideIcon;
  ar: { label: string; sub: string };
  en: { label: string; sub: string };
}

const PAYMENT_METHODS: PaymentMethod[] = [
  {
    id: "bank_transfer",
    icon: Building2,
    ar: { label: "تحويل بنكي", sub: "خلال يوم عمل" },
    en: { label: "Bank Transfer", sub: "Within 1 business day" },
  },
  {
    id: "credit_card",
    icon: CreditCard,
    ar: { label: "بطاقة ائتمانية", sub: "تأكيد فوري" },
    en: { label: "Credit Card", sub: "Instant confirmation" },
  },
  {
    id: "cash",
    icon: Banknote,
    ar: { label: "نقداً", sub: "في الفرع" },
    en: { label: "Cash", sub: "In-branch deposit" },
  },
];

type ModalStep = "form" | "confirm";

interface TopUpModalProps {
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TopUpModal({ onClose }: TopUpModalProps) {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const { toast } = useToast();

  const [step, setStep] = useState<ModalStep>("form");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethodId>("bank_transfer");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  const numAmount = parseFloat(amount) || 0;
  const estimatedFee = (numAmount * FEE_RATE_PERCENT) / 100;
  const totalToReceive = numAmount;
  const canContinue = numAmount > 0;

  // Escape + focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0] ?? null;
        const last = focusable[focusable.length - 1] ?? null;
        if (e.shiftKey) {
          if (document.activeElement === first && last) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last && first) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    amountInputRef.current?.focus();
  }, []);

  const handleContinue = () => {
    const errors: Record<string, string> = {};
    if (numAmount <= 0) errors["amount"] = t(locale, "requiredField");
    if (!paymentMethod) errors["payment_method"] = t(locale, "requiredField");
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setApiError(null);
    setStep("confirm");
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setApiError(null);
    try {
      const res = await topUpWallet({
        amount: numAmount,
        payment_method: paymentMethod,
      });
      if (res.success) {
        toast(t(locale, "topUpPending"), "success");
        onClose();
      } else {
        setApiError(res.message ?? t(locale, "error"));
      }
    } catch {
      setApiError(t(locale, "error"));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMethod = PAYMENT_METHODS.find((m) => m.id === paymentMethod)!;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        dir={isRTL ? "rtl" : "ltr"}
        className="relative w-full max-w-lg bg-card rounded-2xl shadow-xl border border-border overflow-hidden max-h-[92vh] flex flex-col"
      >
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <Wallet className="w-4 h-4 text-foreground-secondary" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-base text-foreground leading-tight">
                {step === "form" ? t(locale, "topUpWallet") : t(locale, "confirmTopUp")}
              </h2>
              <p className="text-[11px] text-foreground-secondary mt-0.5">
                {isRTL ? `الخطوة ${step === "form" ? "١" : "٢"} من ٢` : `Step ${step === "form" ? "1" : "2"} of 2`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors"
            aria-label={t(locale, "cancelAction")}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* ── Body (scrollable) ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-6">
          {step === "form" ? (
            <>
              {/* Amount section */}
              <Section
                num="01"
                title={isRTL ? "المبلغ" : "Amount"}
                hint={isRTL ? "بالـ NC" : "in NC"}
              >
                {/* Big amount display */}
                <div className="flex items-center gap-2">
                  <input
                    ref={amountInputRef}
                    type="number"
                    min="0.01"
                    step="0.01"
                    dir="ltr"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      if (fieldErrors["amount"])
                        setFieldErrors((p) => ({ ...p, amount: "" }));
                    }}
                    placeholder="0.00"
                    className="w-full h-9 px-3 bg-transparent outline-none border border-border rounded-lg focus:border-primary transition-colors text-sm text-foreground placeholder:text-foreground-secondary/40 tabular-nums"
                  />
                  <span className="shrink-0 text-[12px] text-foreground-secondary">NC</span>
                </div>
                {fieldErrors["amount"] && (
                  <p className="mt-2 font-sans text-[10px] tracking-widest text-red-500 uppercase">
                    {fieldErrors["amount"]}
                  </p>
                )}

                {/* Preset chips */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {PRESET_AMOUNTS.map((preset) => {
                    const active = amount === String(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setAmount(String(preset))}
                        className={cn(
                          "px-3.5 py-2 rounded-full text-[12px] font-bold border transition-all active:scale-[0.97]",
                          "tabular-nums",
                          active
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "bg-white border-neutral-200 text-foreground hover:border-neutral-400",
                        )}
                      >
                        {preset.toLocaleString()}
                      </button>
                    );
                  })}
                </div>
              </Section>

              {/* Payment method section */}
              <Section
                num="02"
                title={isRTL ? "وسيلة الدفع" : "Payment method"}
                hint={isRTL ? "اختر واحداً" : "Pick one"}
              >
                <div className="space-y-2">
                  {PAYMENT_METHODS.map((m) => {
                    const active = paymentMethod === m.id;
                    const labels = isRTL ? m.ar : m.en;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPaymentMethod(m.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all text-start",
                          "active:scale-[0.99]",
                          active
                            ? "bg-primary/5 border-primary"
                            : "bg-white border-neutral-200 hover:border-neutral-400",
                        )}
                      >
                        <div
                          className={cn(
                            "shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors",
                            active
                              ? "bg-primary text-white"
                              : "bg-neutral-100 text-foreground-secondary",
                          )}
                        >
                          <m.icon className="w-5 h-5" strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              "text-[14px] font-bold leading-tight",
                              active ? "text-primary" : "text-foreground",
                            )}
                          >
                            {labels.label}
                          </p>
                          <p className="font-sans text-[10px] tracking-widest text-foreground-secondary uppercase mt-0.5">
                            {labels.sub}
                          </p>
                        </div>
                        <div
                          className={cn(
                            "shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                            active
                              ? "border-primary bg-primary"
                              : "border-neutral-300 bg-white",
                          )}
                        >
                          {active && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Section>
            </>
          ) : (
            /* ── Confirm step ── */
            <>
              <Section
                num="01"
                title={isRTL ? "ملخّص الطلب" : "Order summary"}
                hint={isRTL ? "راجع قبل التأكيد" : "Review before confirming"}
              >
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  {/* Big amount block */}
                  <div className="px-5 py-4 border-b border-border">
                    <p className="text-[11px] text-foreground-secondary mb-1">
                      {isRTL ? "ستستلم" : "You'll receive"}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display font-semibold text-xl text-foreground tabular-nums">
                        {formatCurrency(totalToReceive, locale)}
                      </span>
                      <span className="text-[12px] text-foreground-secondary uppercase">
                        NC
                      </span>
                    </div>
                  </div>

                  <SummaryRow
                    label={isRTL ? "المبلغ" : "Amount"}
                    value={`${formatCurrency(numAmount, locale)} NC`}
                  />
                  <SummaryRow
                    label={isRTL ? "وسيلة الدفع" : "Method"}
                    value={
                      isRTL ? selectedMethod.ar.label : selectedMethod.en.label
                    }
                    icon={selectedMethod.icon}
                  />
                  <SummaryRow
                    label={isRTL ? "الرسوم" : "Fee"}
                    value={`${formatCurrency(estimatedFee, locale)} (${FEE_RATE_PERCENT}%)`}
                  />
                </div>
              </Section>

              {/* Info note */}
              <p className="text-[12px] text-foreground-secondary leading-relaxed">
                {isRTL
                  ? "سيُراجع طلبك خلال يوم عمل ثم يُضاف الرصيد إلى محفظتك."
                  : "Your request will be reviewed within 1 business day, then the credit will be added to your wallet."}
              </p>

              {apiError && (
                <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3">
                  <p className="font-sans text-[11px] tracking-widest text-red-500 uppercase">
                    {apiError}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer actions ─────────────────────────────────────────── */}
        <div className="border-t border-border px-6 md:px-7 py-4 flex gap-3 bg-card">
          {step === "form" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-12 rounded-pill border border-border text-foreground text-[14px] font-bold hover:bg-muted transition-colors"
              >
                {t(locale, "cancelAction")}
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className={cn(
                  "flex-1 h-12 rounded-pill inline-flex items-center justify-center gap-2",
                  "bg-primary hover:bg-primary-dark text-white",
                  "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <span>{isRTL ? "متابعة" : "Continue"}</span>
                {isRTL ? (
                  <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep("form")}
                disabled={submitting}
                className="flex-1 h-12 rounded-pill border border-border text-foreground text-[14px] font-bold hover:bg-muted transition-colors disabled:opacity-50"
              >
                {t(locale, "back")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className={cn(
                  "flex-1 h-12 rounded-pill inline-flex items-center justify-center gap-2",
                  "bg-primary hover:bg-primary-dark text-white",
                  "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                )}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    <span>
                      {isRTL ? "جارٍ المعالجة..." : "Processing..."}
                    </span>
                  </span>
                ) : (
                  <>
                    <span>{t(locale, "confirm")}</span>
                    <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Section({
  num,
  title,
  hint,
  children,
}: {
  num: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-primary">
          {num} · {title}
        </span>
        {hint && (
          <span className="font-sans text-[10px] tracking-widest text-foreground-secondary/70 uppercase">
            {hint}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function SummaryRow({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-200 first:border-t-0">
      <span className="font-sans text-[10px] tracking-[0.2em] text-foreground-secondary uppercase">
        {label}
      </span>
      <span className="inline-flex items-center gap-2 text-[14px] font-bold text-foreground">
        {Icon && (
          <Icon
            className="w-4 h-4 text-foreground-secondary"
            strokeWidth={1.8}
          />
        )}
        {value}
      </span>
    </div>
  );
}
